import NightsStayOutlinedIcon from "@mui/icons-material/NightsStayOutlined";
import WbSunnyOutlinedIcon from "@mui/icons-material/WbSunnyOutlined";
import WbTwilightIcon from "@mui/icons-material/WbTwilight";
import { Box, type SxProps, type Theme } from "@mui/material";
import type { TFunction } from "i18next";
import { type FC, Fragment } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE, HOLIDAY_GROUPS, INK } from "../../../theme/tokens";
import { type Holiday, HolidayDayPart } from "./holidayApi";
import { DAY_PART_LABEL_KEY, holidayPartsRange, NO_DAY_PART_KEY, PART_DAY_PART, PART_SHORT_LABEL_KEY } from "./holidayFormat";

const ICONS = {
	[HolidayDayPart.Day]: WbSunnyOutlinedIcon,
	[HolidayDayPart.Evening]: WbTwilightIcon,
	[HolidayDayPart.Night]: NightsStayOutlinedIcon,
} as const;

type Props =
	| {
			/** Null = a grouped person with no shifts in the last 6 weeks. */
			dayPart: HolidayDayPart | null;
			iconOnly?: false;
			sx?: SxProps<Theme>;
			dataTestId?: string;
	  }
	| {
			dayPart: HolidayDayPart;
			/** Just the glyph, for next to a label that already names the day part. */
			iconOnly: true;
	  }
	| {
			/** A holiday's first and last shift, each with its glyph. */
			holiday: Pick<Holiday, "firstPart" | "lastPart">;
	  };

/** Sun, twilight or moon, in the day part's ink. Decorative: a label beside it says the same. */
const renderIcon = (dayPart: HolidayDayPart) => {
	const Icon = ICONS[dayPart];
	return <Icon aria-hidden fontSize="inherit" sx={{ color: HOLIDAY_GROUPS.DAY_PART_INK[dayPart], flexShrink: 0 }} />;
};

/** "☾ Evening before to ☾ Night" — the first and last shift of a holiday, each with its glyph. */
const renderHolidayParts = (t: TFunction, holiday: Pick<Holiday, "firstPart" | "lastPart">) => (
	<Box
		component="span"
		sx={{ display: "inline-flex", alignItems: "center", flexWrap: "wrap", columnGap: 0.75, fontSize: FONT_SIZE.SECONDARY, color: INK.SECONDARY }}
	>
		{holidayPartsRange(holiday).map((part, index) => (
			<Fragment key={part}>
				{index > 0 && (
					<Box component="span" sx={{ color: INK.MUTED }}>
						{t("holidays.part.to")}
					</Box>
				)}
				<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.375 }}>
					{renderIcon(PART_DAY_PART[part])}
					{t(PART_SHORT_LABEL_KEY[part])}
				</Box>
			</Fragment>
		))}
	</Box>
);

/** "☀ Day", a muted "No day part", just the glyph with `iconOnly`, or a holiday's first to last shift with `holiday`. */
export const HolidayDayPartLabel: FC<Props> = (props) => {
	const { t } = useTranslation();
	if ("holiday" in props) return renderHolidayParts(t, props.holiday);
	if (props.iconOnly) return renderIcon(props.dayPart);
	const { dayPart, sx, dataTestId } = props;
	if (dayPart === null) {
		return (
			<Box component="span" data-testid={dataTestId} sx={[{ color: INK.MUTED }, ...(Array.isArray(sx) ? sx : [sx])]}>
				{t(NO_DAY_PART_KEY)}
			</Box>
		);
	}
	return (
		<Box
			component="span"
			data-testid={dataTestId}
			sx={[{ display: "inline-flex", alignItems: "center", gap: 0.5, color: INK.PRIMARY, fontWeight: 500 }, ...(Array.isArray(sx) ? sx : [sx])]}
		>
			{renderIcon(dayPart)}
			{t(DAY_PART_LABEL_KEY[dayPart])}
		</Box>
	);
};
